import { useEffect } from 'react';
import { createSafeResizeObserver } from '@shared/utils/createSafeResizeObserver';
import { withWindow } from '@shared/utils/withWindow';

export const placeCaretAtEnd = (element: HTMLElement): void => {
  withWindow(globalWindow => {
    const selection = globalWindow.getSelection?.();
    if (!selection) return;
    const range = globalWindow.document.createRange();
    range.selectNodeContents(element);
    range.collapse(false);
    selection.removeAllRanges();
    selection.addRange(range);
  });
};

const focusAtEnd = (element: HTMLElement) => {
  element.focus();
  placeCaretAtEnd(element);
};

const isLaidOut = (element: HTMLElement) => element.getClientRects().length > 0;

/**
 * Focuses the editable (caret at end) once it is laid out. On a cold inline-edit open, Suspense
 * mounts the dialog under display:none while a chunk loads, and focus() there is a silent no-op.
 */
export const useFocusEditableWhenVisible = (ref: React.RefObject<HTMLElement> | undefined, isContentEditable: boolean): void => {
  useEffect(() => {
    const element = ref?.current;
    if (!element || !isContentEditable) return;

    if (isLaidOut(element)) {
      focusAtEnd(element);
      return;
    }

    // Fires when the hidden ancestor is revealed: the element goes from no box to a sized one
    const observer = createSafeResizeObserver(
      () => {
        if (!isLaidOut(element)) return;
        observer.disconnect();
        focusAtEnd(element);
      },
      { throttle: false },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [ref, isContentEditable]);
};
